import { useEffect, useMemo, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import {
  Download,
  LayoutGrid,
  Link as LinkIcon,
  List as ListIcon,
  Network,
  Search,
  Sparkles,
  Trash2,
  Upload,
  X,
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { TagBadge } from "@/components/ui/TagBadge";
import { ConfirmDialog } from "@/components/shared/ConfirmDialog";
import { cn } from "@/lib/utils";
import {
  formatFileSize,
  useAddExternalLink,
  useBulkDeleteMedia,
  useDownloadFile,
  useMediaList,
  useMediaTags,
  useUploadFile,
} from "../hooks/useMedia";
import {
  hasDirectAccessTo,
  useMyAccessScope,
} from "@/modules/auth/hooks/useMyAccessScope";
import { mediaNeedsAttention, type MediaFile } from "../types";
import { FileCard } from "./FileCard";
import { FileTypeIcon } from "./FileTypeIcon";
import { DocumentTreeView } from "./DocumentTreeView";

type EntityType = "COMPANY" | "PRODUCT" | "PROJECT";
type ViewMode = "grid" | "list" | "tree";

type Props = {
  entityType: EntityType;
  entityId: string;
  /** Human-readable label of the entity (used as the entityLabel on upload). */
  entityLabel?: string;
  /** Hide Tree view toggle (used on Project where there is no useful subtree). */
  hideTree?: boolean;
  /** Initial view mode — falls through to localStorage, then sensible default. */
  defaultView?: ViewMode;
};

const ROLLUP_KEY = (et: EntityType) =>
  `phazex.documents.includeChildren.${et}`;
const VIEW_KEY = (et: EntityType) => `phazex.documents.view.${et}`;

const FILE_TYPES: { value: string; label: string }[] = [
  { value: "", label: "All Types" },
  { value: "document", label: "Document" },
  { value: "image", label: "Image" },
  { value: "video", label: "Video" },
  { value: "audio", label: "Audio" },
  { value: "spreadsheet", label: "Spreadsheet" },
  { value: "presentation", label: "Presentation" },
  { value: "archive", label: "Archive" },
  { value: "code", label: "Code" },
  { value: "link", label: "Link" },
  { value: "other", label: "Other" },
];

export function EntityDocumentsTab({
  entityType,
  entityId,
  entityLabel,
  hideTree = false,
  defaultView,
}: Props) {
  const navigate = useNavigate();
  const fileInputRef = useRef<HTMLInputElement | null>(null);

  const initialView: ViewMode = (() => {
    if (typeof window !== "undefined") {
      const stored = window.localStorage.getItem(VIEW_KEY(entityType));
      if (stored === "grid" || stored === "list" || stored === "tree") {
        if (stored === "tree" && hideTree) {
          // Project default fallback
        } else {
          return stored;
        }
      }
    }
    if (defaultView) return defaultView;
    return entityType === "PROJECT" ? "grid" : "tree";
  })();

  const [viewMode, setViewMode] = useState<ViewMode>(initialView);
  const [includeChildren, setIncludeChildren] = useState<boolean>(() => {
    if (typeof window === "undefined") return false;
    return window.localStorage.getItem(ROLLUP_KEY(entityType)) === "true";
  });
  const [search, setSearch] = useState("");
  const [fileType, setFileType] = useState("");
  const [tagFilter, setTagFilter] = useState<string[]>([]);
  const [selected, setSelected] = useState<Set<string>>(new Set());
  const [linkOpen, setLinkOpen] = useState(false);
  const [deleteTarget, setDeleteTarget] = useState<
    | { kind: "one"; file: MediaFile }
    | { kind: "bulk"; ids: string[] }
    | null
  >(null);
  const [linkForm, setLinkForm] = useState({ title: "", url: "", description: "" });

  const tagOptions = useMediaTags().data ?? [];

  // Persist preferences
  useEffect(() => {
    if (typeof window === "undefined") return;
    window.localStorage.setItem(VIEW_KEY(entityType), viewMode);
  }, [viewMode, entityType]);
  useEffect(() => {
    if (typeof window === "undefined") return;
    window.localStorage.setItem(
      ROLLUP_KEY(entityType),
      includeChildren ? "true" : "false",
    );
  }, [includeChildren, entityType]);

  // RLAC: a Tier-3 user navigating to a parent entity (only visible because
  // a child is assigned) must NOT get rollup tools — those would turn the
  // parent page into a side-channel query interface for accessible children.
  // Hide the "Include children" toggle and Tree view button, and force the
  // view to grid/list. Tier 1/2 are unaffected.
  const myScope = useMyAccessScope();
  const hasDirectAccess = hasDirectAccessTo(myScope.data, entityType, entityId);

  // Clamp the persisted view back to grid for nav-only users (their stored
  // preference may have been "tree" from a session with broader access).
  const effectiveViewMode: ViewMode =
    !hasDirectAccess && viewMode === "tree" ? "grid" : viewMode;

  // Roll-up makes no sense on PROJECT — clamp to false. Same for nav-only
  // users on Company/Product, where the toggle is hidden anyway.
  const effectiveIncludeChildren =
    entityType === "PROJECT" || !hasDirectAccess ? false : includeChildren;

  const listParams = useMemo(
    () => ({
      entityType,
      entityId,
      includeChildren: effectiveIncludeChildren,
      search: search || undefined,
      fileType: fileType || undefined,
      tags: tagFilter.length > 0 ? tagFilter : undefined,
      limit: 100,
    }),
    [entityType, entityId, effectiveIncludeChildren, search, fileType, tagFilter],
  );

  const { data, isLoading } = useMediaList(
    effectiveViewMode === "tree" ? {} : listParams,
  );

  const uploader = useUploadFile();
  const addLink = useAddExternalLink();
  const bulkDelete = useBulkDeleteMedia();
  const downloadMutation = useDownloadFile();

  const files = data?.data ?? [];

  const showRollupToggle =
    entityType !== "PROJECT" && effectiveViewMode !== "tree" && hasDirectAccess;
  const showTreeButton = !hideTree && hasDirectAccess;

  function toggleSelect(file: MediaFile) {
    setSelected((s) => {
      const next = new Set(s);
      if (next.has(file.id)) next.delete(file.id);
      else next.add(file.id);
      return next;
    });
  }

  // Map the current entity context to the right linked-id array so the
  // uploaded file is considered linked from the moment it's saved (and
  // the needs-attention indicator stays off). The entityType branch
  // mirrors the multi-select Linked To picker on the View File edit form.
  const contextLinkIds = (() => {
    if (entityType === "COMPANY") return { companyIds: [entityId] };
    if (entityType === "PRODUCT") return { productIds: [entityId] };
    if (entityType === "PROJECT") return { projectIds: [entityId] };
    return {};
  })();

  async function onFileInputChange(e: React.ChangeEvent<HTMLInputElement>) {
    const fl = e.target.files;
    if (!fl || fl.length === 0) return;
    for (const f of Array.from(fl)) {
      await uploader.mutate({
        file: f,
        entityType,
        entityId,
        entityLabel,
        ...contextLinkIds,
      });
    }
    e.target.value = "";
  }

  async function submitLink(e: React.FormEvent) {
    e.preventDefault();
    if (!linkForm.title.trim() || !linkForm.url.trim()) return;
    await addLink.mutateAsync({
      title: linkForm.title.trim(),
      externalUrl: linkForm.url.trim(),
      description: linkForm.description.trim() || undefined,
      entityType,
      entityId,
      entityLabel,
      ...contextLinkIds,
    });
    setLinkForm({ title: "", url: "", description: "" });
    setLinkOpen(false);
  }

  return (
    <div className="space-y-3">
      <input
        ref={fileInputRef}
        type="file"
        multiple
        className="hidden"
        onChange={onFileInputChange}
      />

      {/* Toolbar */}
      <div className="flex items-center gap-2 flex-wrap">
        {effectiveViewMode !== "tree" && (
          <>
            <div className="relative flex-1 min-w-[220px] max-w-[320px]">
              <Search
                size={14}
                className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground"
              />
              <Input
                placeholder="Search documents..."
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                className="pl-8"
              />
            </div>

            <select
              value={fileType}
              onChange={(e) => setFileType(e.target.value)}
              className="h-9 rounded-md border bg-white px-2 text-sm"
            >
              {FILE_TYPES.map((t) => (
                <option key={t.value} value={t.value}>
                  {t.label}
                </option>
              ))}
            </select>
          </>
        )}

        {showRollupToggle && (
          <label className="flex items-center gap-1.5 h-9 px-3 rounded-md border bg-white text-sm cursor-pointer">
            <input
              type="checkbox"
              checked={includeChildren}
              onChange={(e) => setIncludeChildren(e.target.checked)}
              className="h-3.5 w-3.5"
            />
            Include children
          </label>
        )}

        <div className="ml-auto flex items-center gap-2">
          <div className="flex rounded-md border bg-white overflow-hidden">
            <button
              type="button"
              onClick={() => setViewMode("grid")}
              className={cn(
                "h-9 w-9 flex items-center justify-center",
                effectiveViewMode === "grid"
                  ? "bg-primary text-primary-foreground"
                  : "hover:bg-muted",
              )}
              aria-label="Grid view"
              title="Grid"
            >
              <LayoutGrid size={14} strokeWidth={1.5} />
            </button>
            <button
              type="button"
              onClick={() => setViewMode("list")}
              className={cn(
                "h-9 w-9 flex items-center justify-center border-l",
                effectiveViewMode === "list"
                  ? "bg-primary text-primary-foreground"
                  : "hover:bg-muted",
              )}
              aria-label="List view"
              title="List"
            >
              <ListIcon size={14} strokeWidth={1.5} />
            </button>
            {showTreeButton && (
              <button
                type="button"
                onClick={() => setViewMode("tree")}
                className={cn(
                  "h-9 w-9 flex items-center justify-center border-l",
                  effectiveViewMode === "tree"
                    ? "bg-primary text-primary-foreground"
                    : "hover:bg-muted",
                )}
                aria-label="Tree view"
                title="Tree"
              >
                <Network size={14} strokeWidth={1.5} />
              </button>
            )}
          </div>

          <Button type="button" onClick={() => fileInputRef.current?.click()}>
            <Upload size={14} strokeWidth={1.5} /> Upload
          </Button>
          <Button
            type="button"
            variant="outline"
            onClick={() => setLinkOpen(true)}
          >
            <LinkIcon size={14} strokeWidth={1.5} /> Add Link
          </Button>
        </div>
      </div>

      {/* Tag filter chips */}
      {effectiveViewMode !== "tree" && tagOptions.length > 0 && (
        <div className="flex flex-wrap items-center gap-1.5">
          <span className="text-[11px] font-medium uppercase text-muted-foreground">
            Tags
          </span>
          {tagOptions.map((opt) => {
            const active = tagFilter.includes(opt.value);
            return (
              <button
                key={opt.id}
                type="button"
                onClick={() =>
                  setTagFilter((prev) =>
                    prev.includes(opt.value)
                      ? prev.filter((v) => v !== opt.value)
                      : [...prev, opt.value],
                  )
                }
                style={{ opacity: active ? 1 : 0.5 }}
              >
                <TagBadge value={opt.value} label={opt.label} size="sm" />
              </button>
            );
          })}
          {tagFilter.length > 0 && (
            <button
              type="button"
              onClick={() => setTagFilter([])}
              className="text-[11px] text-muted-foreground underline"
            >
              clear
            </button>
          )}
        </div>
      )}

      {selected.size > 0 && effectiveViewMode !== "tree" && (
        <div className="flex items-center gap-2 rounded-md border bg-blue-50 px-3 py-2 text-sm">
          <span className="font-semibold">{selected.size} selected</span>
          <div className="flex-1" />
          <Button
            variant="outline"
            size="sm"
            onClick={async () => {
              for (const id of selected) {
                const f = files.find((x) => x.id === id);
                if (!f) continue;
                await downloadMutation.mutateAsync({
                  id: f.id,
                  originalName: f.originalName,
                  isExternal: f.isExternal,
                  externalUrl: f.externalUrl,
                });
              }
            }}
          >
            <Download size={12} strokeWidth={1.5} /> Download
          </Button>
          <Button
            variant="outline"
            size="sm"
            className="text-destructive hover:text-destructive"
            onClick={() => {
              if (selected.size === 0) return;
              setDeleteTarget({ kind: "bulk", ids: Array.from(selected) });
            }}
          >
            <Trash2 size={12} strokeWidth={1.5} /> Delete
          </Button>
          <Button variant="ghost" size="sm" onClick={() => setSelected(new Set())}>
            <X size={12} strokeWidth={1.5} /> Clear
          </Button>
        </div>
      )}

      {/* Body */}
      {effectiveViewMode === "tree" ? (
        <DocumentTreeView
          rootEntityType={entityType}
          rootEntityId={entityId}
        />
      ) : (
        <div
          style={{
            background: "white",
            border: "1px solid hsl(210,30%,85%)",
            borderRadius: "0.75rem",
          }}
        >
          {isLoading ? (
            <div className="flex items-center justify-center h-40 text-sm text-muted-foreground">
              Loading documents…
            </div>
          ) : files.length === 0 ? (
            <div className="flex flex-col items-center justify-center py-16 text-center text-sm text-muted-foreground">
              No documents attached to this {entityType.toLowerCase()} yet.
              <Button
                size="sm"
                className="mt-3"
                onClick={() => fileInputRef.current?.click()}
              >
                <Upload size={12} strokeWidth={1.5} /> Upload First Document
              </Button>
            </div>
          ) : effectiveViewMode === "grid" ? (
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-3 p-3">
              {files.map((f) => (
                <FileCard
                  key={f.id}
                  file={f}
                  isSelected={selected.has(f.id)}
                  onSelect={() => toggleSelect(f)}
                  onOpen={(file) => navigate(`/media-vault/${file.id}`)}
                  onDownload={(file) =>
                    downloadMutation.mutate({
                      id: file.id,
                      originalName: file.originalName,
                      isExternal: file.isExternal,
                      externalUrl: file.externalUrl,
                    })
                  }
                  onDelete={(file) => setDeleteTarget({ kind: "one", file })}
                  tagOptions={tagOptions}
                />
              ))}
            </div>
          ) : (
            <DocsListTable
              files={files}
              selected={selected}
              onToggle={toggleSelect}
              onOpen={(f) => navigate(`/media-vault/${f.id}`)}
              onDownload={(f) =>
                downloadMutation.mutate({
                  id: f.id,
                  originalName: f.originalName,
                  isExternal: f.isExternal,
                  externalUrl: f.externalUrl,
                })
              }
              onDelete={(f) => setDeleteTarget({ kind: "one", file: f })}
            />
          )}
        </div>
      )}

      <ConfirmDialog
        open={deleteTarget !== null}
        onOpenChange={(open) => {
          if (!open) setDeleteTarget(null);
        }}
        title={
          deleteTarget?.kind === "bulk" ? "Delete files?" : "Delete file?"
        }
        description={(() => {
          if (!deleteTarget) return "";
          if (deleteTarget.kind === "bulk") {
            const n = deleteTarget.ids.length;
            return `Are you sure you want to delete ${n} selected file${n === 1 ? "" : "s"}? They will be removed from your Media Vault.`;
          }
          return `Are you sure you want to delete '${deleteTarget.file.title}'? It will be removed from your Media Vault.`;
        })()}
        onConfirm={async () => {
          if (!deleteTarget) return;
          if (deleteTarget.kind === "one") {
            await bulkDelete.mutateAsync([deleteTarget.file.id]);
          } else {
            await bulkDelete.mutateAsync(deleteTarget.ids);
            setSelected(new Set());
          }
        }}
      />

      {/* Add link modal */}
      <Dialog open={linkOpen} onOpenChange={setLinkOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Add External Link</DialogTitle>
          </DialogHeader>
          <form onSubmit={submitLink} className="space-y-3">
            <div>
              <label className="text-xs font-medium">Title</label>
              <Input
                value={linkForm.title}
                onChange={(e) =>
                  setLinkForm((f) => ({ ...f, title: e.target.value }))
                }
                required
              />
            </div>
            <div>
              <label className="text-xs font-medium">URL</label>
              <Input
                type="url"
                value={linkForm.url}
                onChange={(e) =>
                  setLinkForm((f) => ({ ...f, url: e.target.value }))
                }
                required
                placeholder="https://…"
              />
            </div>
            <div>
              <label className="text-xs font-medium">Description</label>
              <textarea
                value={linkForm.description}
                onChange={(e) =>
                  setLinkForm((f) => ({ ...f, description: e.target.value }))
                }
                rows={2}
                className="mt-1 w-full rounded border px-2 py-1.5 text-sm resize-none"
              />
            </div>
            <div className="flex justify-end gap-2">
              <Button
                type="button"
                variant="outline"
                onClick={() => setLinkOpen(false)}
              >
                Cancel
              </Button>
              <Button type="submit" disabled={addLink.isPending}>
                Add Link
              </Button>
            </div>
          </form>
        </DialogContent>
      </Dialog>
    </div>
  );
}

function DocsListTable({
  files,
  selected,
  onToggle,
  onOpen,
  onDownload,
  onDelete,
}: {
  files: MediaFile[];
  selected: Set<string>;
  onToggle: (f: MediaFile) => void;
  onOpen: (f: MediaFile) => void;
  onDownload: (f: MediaFile) => void;
  onDelete: (f: MediaFile) => void;
}) {
  return (
    <table className="w-full text-sm">
      <thead className="bg-muted text-[11px] uppercase text-muted-foreground">
        <tr>
          <th className="w-10 px-2 py-2" />
          <th className="px-3 py-2 text-left">File</th>
          <th className="px-3 py-2 text-left">Type</th>
          <th className="px-3 py-2 text-left">Size</th>
          <th className="px-3 py-2 text-left">Attached To</th>
          <th className="px-3 py-2 text-left">Uploaded By</th>
          <th className="px-3 py-2 text-left">Date</th>
          <th className="w-20 px-3 py-2 text-right">Actions</th>
        </tr>
      </thead>
      <tbody>
        {files.map((f) => (
          <tr
            key={f.id}
            className="border-t hover:bg-muted/40 cursor-pointer"
            onClick={() => onOpen(f)}
          >
            <td className="px-2 py-2" onClick={(e) => e.stopPropagation()}>
              <input
                type="checkbox"
                checked={selected.has(f.id)}
                onChange={() => onToggle(f)}
                className="h-4 w-4"
              />
            </td>
            <td className="px-3 py-2">
              <div className="flex items-center gap-2 min-w-0">
                <FileTypeIcon fileType={f.fileType} size={24} />
                <div className="min-w-0">
                  <div className="font-medium truncate flex items-center gap-1.5">
                    {mediaNeedsAttention(f) && (
                      <span
                        className="inline-flex h-4 w-4 shrink-0 items-center justify-center rounded-full bg-amber-100 text-amber-600"
                        title="Missing details — click to add"
                        aria-label="Missing details"
                      >
                        <Sparkles size={10} strokeWidth={2} />
                      </span>
                    )}
                    <span className="truncate">{f.title}</span>
                  </div>
                  <div className="text-[11px] text-muted-foreground truncate">
                    {f.originalName}
                  </div>
                </div>
              </div>
            </td>
            <td className="px-3 py-2">
              <span className="text-xs rounded bg-muted px-2 py-0.5">
                {f.fileType}
              </span>
            </td>
            <td className="px-3 py-2 text-xs">{formatFileSize(f.fileSize)}</td>
            <td className="px-3 py-2 text-xs">
              {f.entityLabel ?? f.entityType ?? "—"}
            </td>
            <td className="px-3 py-2 text-xs">{f.uploadedByName ?? "—"}</td>
            <td className="px-3 py-2 text-xs">
              {new Date(f.createdAt).toLocaleDateString()}
            </td>
            <td
              className="px-3 py-2 text-right"
              onClick={(e) => e.stopPropagation()}
            >
              <button
                type="button"
                onClick={() => onDownload(f)}
                className="p-1 rounded hover:bg-muted"
                aria-label="Download"
              >
                <Download size={14} strokeWidth={1.5} />
              </button>
              <button
                type="button"
                onClick={() => onDelete(f)}
                className="p-1 rounded hover:bg-muted text-destructive"
                aria-label="Delete"
              >
                <Trash2 size={14} strokeWidth={1.5} />
              </button>
            </td>
          </tr>
        ))}
      </tbody>
    </table>
  );
}
